import type { MockDatabase, MockRecord } from './database.ts';
import { seedId } from './seed.ts';
import { syncVendorRoleJoins } from './vendorFixtures.ts';

export const LEGACY_VENDOR_USERS_ID = 'legacy-vendor-users-1';

interface LegacyAccount { userName: string; firstName: string; lastName: string; email: string }

/** Legacy Manage Users rows per supplier: the IsVendor login becomes the company account, the rest are its contacts. */
export const LEGACY_VENDOR_ACCOUNTS: { company: string; login: LegacyAccount; contacts: LegacyAccount[] }[] = [
  { company: 'ENROIL OILFIELD SOLUTIONS AND SERVICES LLC',
    login: { userName: '1286532', firstName: 'Saranga', lastName: 'Rajan', email: 'saranga.rajan@enroil.com' },
    contacts: [
      { userName: 'saranga.rajan@enroil.com', firstName: 'Saranga', lastName: 'Rajan', email: 'saranga.rajan@enroil.com' },
      { userName: 'jcm@enroil.com', firstName: 'JAICHANDRA', lastName: 'Mada', email: 'jcm@enroil.com' },
    ] },
  { company: 'OPTIMIZE DRILLING FLUIDS',
    login: { userName: '1273759', firstName: 'Medhat', lastName: 'Saleh', email: 'msaleh@optimize-dfs.com' },
    contacts: [
      { userName: 'msaleh@optimize-dfs.com', firstName: 'Medhat', lastName: 'Saleh', email: 'msaleh@optimize-dfs.com' },
    ] },
  { company: 'Juhua Group (Hongkong) Limited',
    login: { userName: '1280737', firstName: 'Jimmy', lastName: 'Wang', email: 'jckzw@juhua.com' },
    contacts: [
      { userName: 'jckzw@juhua.com', firstName: 'Judy', lastName: 'Zhu', email: 'jckzw@juhua.com' },
      { userName: 'jckwzm@juhua.com.cn', firstName: 'Jimmy', lastName: 'Wang', email: 'jckwzm@juhua.com.cn' },
    ] },
  { company: 'PACIFIC COAST RESOURCES CORPORATION',
    login: { userName: '1304525', firstName: 'Craig', lastName: 'Micek', email: 'craig@cropfood.com' },
    contacts: [
      { userName: 'craig@cropfood.com', firstName: 'Craig', lastName: 'Micek', email: 'craig@cropfood.com' },
    ] },
];

const SEEDED_CONTACT = /^vendor\d+\.user\d+@example\.test$/i;
// Workflow rows copy the supplier contact's name and email when they are created.
const WORKFLOW_TABLES = ['vqcas', 'vendorRequests', 'coas', 'samples', 'coqs', 'packages', 'shipments', 'suppliers'];

/** Replaces the seeded mock supplier contacts with the legacy vendor users; runs once per database. */
export function applyLegacyVendorUsers(database: MockDatabase): boolean {
  if (database.businessDemoSeed?.some((row) => row.oid === LEGACY_VENDOR_USERS_ID)) return false;
  const users = database.users ?? [];
  const removed = new Set<string>();
  const added: MockRecord[] = [];
  let slot = 0;
  for (const entry of LEGACY_VENDOR_ACCOUNTS) {
    const company = users.find((user) => user.portal === 'QCDOC-VENDOR' && user.isVendor === true && user.company === entry.company);
    if (!company) continue;
    const { login } = entry;
    const fullName = `${login.firstName} ${login.lastName}`;
    const identity = { userName: login.userName, email: login.email, firstName: login.firstName, lastName: login.lastName, fullName,
      venEmail: login.email, VenEmail: login.email, secondVenEmail: '', secondaryEmail: '', secondEmail: '', isLegacyVendorLogin: true };
    for (const record of [company, ...(database.vendors ?? []).filter((vendor) => vendor.oid === company.oid)]) Object.assign(record, identity);

    for (const user of users) {
      if (user.isUser === true && user.vendorId === company.oid && SEEDED_CONTACT.test(String(user.userName))) removed.add(user.oid);
    }
    for (const account of entry.contacts) {
      const oid = seedId(130, slot++);
      if (users.some((user) => user.oid === oid)) continue;
      const contact: MockRecord = { ...company, oid, userId: oid, providerUserKey: oid, vendorId: company.oid,
        isVendor: false, isUser: true, isInternal: false, isApproved: true, isActive: true, isLockedOut: false,
        userName: account.userName, email: account.email, firstName: account.firstName, lastName: account.lastName,
        fullName: `${account.firstName} ${account.lastName}`, venEmail: account.email, VenEmail: account.email,
        roles: ['External User'], packaging: { read: true, write: false, admin: false } };
      for (const key of ['roleAssignments', 'password', 'isLegacyVendorLogin']) delete contact[key];
      added.push(contact);
    }

    const contactColumns = { venFirstName: login.firstName, venLastName: login.lastName, venEmail: login.email,
      vendorFirstName: login.firstName, vendorLastName: login.lastName };
    for (const table of WORKFLOW_TABLES) {
      for (const row of database[table] ?? []) {
        if (row.vendorId !== company.oid) continue;
        for (const [column, value] of Object.entries(contactColumns)) if (column in row) row[column] = value;
      }
    }
    for (const row of database.packages ?? []) if (row.packagerId === company.oid) row.packagerUserName = login.userName;
  }
  database.users = [...users.filter((user) => !removed.has(user.oid)), ...added];
  syncVendorRoleJoins(database);
  (database.businessDemoSeed ??= []).push({ oid: LEGACY_VENDOR_USERS_ID, details: 'Legacy vendor users mapped to their suppliers.' });
  return true;
}
